import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Slider,
  Button,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  FormHelperText,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  CompareArrows as CompareArrowsIcon,
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

interface ValuationAssumptions {
  capRate: { rate: number; noi: number };
  revenueMultiple: { multiple: number; revenue: number };
  dcf: { growthRate: number; discountRate: number; projectionYears: number };
  salesComparison: { pricePerRoom: number; numRooms: number };
  replacementCost: { costPerSqFt: number; depreciation: number; sqFt: number };
}

interface ValuationAssumptionsPanelProps {
  assumptions?: ValuationAssumptions;
  customAssumptions: Partial<ValuationAssumptions>;
  onChange: (method: string, parameter: string, value: number) => void;
  onRecalculate: () => void;
  loading: boolean;
}

const ValuationAssumptionsPanel: React.FC<ValuationAssumptionsPanelProps> = ({
  assumptions,
  customAssumptions,
  onChange,
  onRecalculate,
  loading
}) => {
  const theme = useTheme();
  const [expandedMethod, setExpandedMethod] = useState<string | false>('capRate');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMethod(isExpanded ? panel : false);
  };

  const getCurrentValue = (method: keyof ValuationAssumptions, parameter: string): number => {
    const customValue = customAssumptions[method]?.[parameter as keyof ValuationAssumptions[typeof method]];
    if (customValue !== undefined) return customValue as number;

    const baseValue = assumptions?.[method]?.[parameter as keyof ValuationAssumptions[typeof method]];
    return typeof baseValue === 'number' ? baseValue : 0;
  };

  const getMethodIcon = (methodKey: string) => {
    switch (methodKey.toLowerCase()) {
      case 'caprate': return <TrendingUpIcon />;
      case 'revenuemultiple': return <ShowChartIcon />;
      case 'dcf': return <CalculateIcon />;
      case 'salescomparison': return <CompareArrowsIcon />;
      case 'replacementcost': return <HomeIcon />;
      default: return <AccountBalanceIcon />;
    }
  };

  const methodConfigs = {
    capRate: {
      title: 'Capitalization Rate Method',
      description: 'Adjust the cap rate and Net Operating Income (NOI) parameters',
      icon: <TrendingUpIcon />,
      color: theme.palette.primary.main,
      parameters: [
        {
          key: 'rate',
          label: 'Cap Rate',
          type: 'percentage',
          min: 0.03,
          max: 0.20,
          step: 0.001,
          description: 'Market capitalization rate (3-20%)',
          unit: '%'
        },
        {
          key: 'noi',
          label: 'Net Operating Income',
          type: 'currency',
          min: 0,
          max: 50000000,
          step: 10000,
          description: 'Annual Net Operating Income',
          unit: '$'
        }
      ]
    },
    revenueMultiple: {
      title: 'Revenue Multiple Method',
      description: 'Adjust the revenue multiple and total revenue parameters',
      icon: <ShowChartIcon />,
      color: theme.palette.success.main,
      parameters: [
        {
          key: 'multiple',
          label: 'Revenue Multiple',
          type: 'number',
          min: 1,
          max: 10,
          step: 0.1,
          description: 'Industry revenue multiple (1x-10x)',
          unit: 'x'
        },
        {
          key: 'revenue',
          label: 'Total Revenue',
          type: 'currency',
          min: 0,
          max: 100000000,
          step: 50000,
          description: 'Annual total revenue',
          unit: '$'
        }
      ]
    },
    dcf: {
      title: 'Discounted Cash Flow Method',
      description: 'Adjust growth rate, discount rate, and projection period',
      icon: <CalculateIcon />,
      color: theme.palette.info.main,
      parameters: [
        {
          key: 'growthRate',
          label: 'Growth Rate',
          type: 'percentage',
          min: -0.05,
          max: 0.15,
          step: 0.001,
          description: 'Annual growth rate (-5% to 15%)',
          unit: '%'
        },
        {
          key: 'discountRate',
          label: 'Discount Rate',
          type: 'percentage',
          min: 0.05,
          max: 0.25,
          step: 0.001,
          description: 'Required rate of return (5-25%)',
          unit: '%'
        },
        {
          key: 'projectionYears',
          label: 'Projection Years',
          type: 'number',
          min: 3,
          max: 15,
          step: 1,
          description: 'Number of years to project (3-15)',
          unit: 'years'
        }
      ]
    },
    salesComparison: {
      title: 'Sales Comparison Method',
      description: 'Adjust price per room and number of rooms',
      icon: <CompareArrowsIcon />,
      color: theme.palette.warning.main,
      parameters: [
        {
          key: 'pricePerRoom',
          label: 'Price Per Room',
          type: 'currency',
          min: 25000,
          max: 1000000,
          step: 5000,
          description: 'Market price per room ($25K-$1M)',
          unit: '$'
        },
        {
          key: 'numRooms',
          label: 'Number of Rooms',
          type: 'number',
          min: 1,
          max: 2000,
          step: 1,
          description: 'Total number of rooms',
          unit: 'rooms'
        }
      ]
    },
    replacementCost: {
      title: 'Replacement Cost Method',
      description: 'Adjust construction cost per sqft and depreciation',
      icon: <HomeIcon />,
      color: theme.palette.error.main,
      parameters: [
        {
          key: 'costPerSqFt',
          label: 'Cost Per Sq Ft',
          type: 'currency',
          min: 100,
          max: 1500,
          step: 10,
          description: 'Construction cost per square foot',
          unit: '$/sqft'
        },
        {
          key: 'depreciation',
          label: 'Depreciation',
          type: 'percentage',
          min: 0,
          max: 0.70,
          step: 0.01,
          description: 'Physical depreciation (0-70%)',
          unit: '%'
        },
        {
          key: 'sqFt',
          label: 'Square Footage',
          type: 'number',
          min: 1000,
          max: 1000000,
          step: 1000,
          description: 'Total building square footage',
          unit: 'sqft'
        }
      ]
    }
  };

  const renderParameterControl = (methodKey: string, param: any) => {
    const currentValue = getCurrentValue(methodKey as keyof ValuationAssumptions, param.key);
    const isModified = customAssumptions[methodKey as keyof ValuationAssumptions]?.[param.key as keyof ValuationAssumptions[keyof ValuationAssumptions]] !== undefined;

    const handleChange = (value: number) => {
      onChange(methodKey, param.key, value);
    };

    const displayValue = param.type === 'percentage' ? currentValue * 100 : currentValue;
    const inputValue = param.type === 'percentage' ? currentValue * 100 : currentValue;

    return (
      <Grid item xs={12} sm={6} key={param.key}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {param.label}
              {isModified && (
                <Chip
                  label="Modified"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Tooltip title={param.description}>
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <TextField
            type="number"
            value={inputValue}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value) || 0;
              const finalValue = param.type === 'percentage' ? newValue / 100 : newValue;
              handleChange(finalValue);
            }}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: param.unit && (
                <InputAdornment position="start">{param.unit}</InputAdornment>
              )
            }}
            inputProps={{
              min: param.type === 'percentage' ? param.min * 100 : param.min,
              max: param.type === 'percentage' ? param.max * 100 : param.max,
              step: param.type === 'percentage' ? param.step * 100 : param.step
            }}
          />

          <Slider
            value={displayValue}
            onChange={(_, value) => {
              const finalValue = param.type === 'percentage' ? (value as number) / 100 : value as number;
              handleChange(finalValue);
            }}
            min={param.type === 'percentage' ? param.min * 100 : param.min}
            max={param.type === 'percentage' ? param.max * 100 : param.max}
            step={param.type === 'percentage' ? param.step * 100 : param.step}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              if (param.type === 'percentage') return `${value.toFixed(1)}%`;
              if (param.type === 'currency') return formatCurrency(value);
              return value.toString();
            }}
            sx={{ mt: 1 }}
          />

          <FormHelperText>{param.description}</FormHelperText>
        </Box>
      </Grid>
    );
  };

  const calculatePreview = (methodKey: string) => {
    // Simple preview calculation based on current parameters
    const config = methodConfigs[methodKey as keyof typeof methodConfigs];
    if (!config) return null;

    try {
      switch (methodKey) {
        case 'capRate': {
          const rate = getCurrentValue('capRate', 'rate');
          const noi = getCurrentValue('capRate', 'noi');
          return rate > 0 ? noi / rate : 0;
        }
        case 'revenueMultiple': {
          const multiple = getCurrentValue('revenueMultiple', 'multiple');
          const revenue = getCurrentValue('revenueMultiple', 'revenue');
          return revenue * multiple;
        }
        case 'dcf': {
          const growthRate = getCurrentValue('dcf', 'growthRate');
          const discountRate = getCurrentValue('dcf', 'discountRate');
          const projectionYears = getCurrentValue('dcf', 'projectionYears');
          const noi = getCurrentValue('capRate', 'noi'); // Use NOI from cap rate method

          if (discountRate <= growthRate || discountRate <= 0) return 0;

          let dcfValue = 0;
          for (let i = 1; i <= projectionYears; i++) {
            dcfValue += (noi * Math.pow(1 + growthRate, i)) / Math.pow(1 + discountRate, i);
          }
          return dcfValue;
        }
        case 'salesComparison': {
          const pricePerRoom = getCurrentValue('salesComparison', 'pricePerRoom');
          const numRooms = getCurrentValue('salesComparison', 'numRooms');
          return pricePerRoom * numRooms;
        }
        case 'replacementCost': {
          const costPerSqFt = getCurrentValue('replacementCost', 'costPerSqFt');
          const depreciation = getCurrentValue('replacementCost', 'depreciation');
          const sqFt = getCurrentValue('replacementCost', 'sqFt');
          return (costPerSqFt * sqFt) * (1 - depreciation);
        }
        default:
          return 0;
      }
    } catch (error) {
      return 0;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Valuation Assumptions
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Adjust the key assumptions and parameters used in each valuation method. Changes will be reflected in real-time.
      </Typography>

      {/* Recalculate Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
          onClick={onRecalculate}
          disabled={loading}
          size="large"
        >
          {loading ? 'Recalculating...' : 'Recalculate Valuations'}
        </Button>
      </Box>

      {/* Method Accordions */}
      {Object.entries(methodConfigs).map(([methodKey, config]) => (
        <Accordion
          key={methodKey}
          expanded={expandedMethod === methodKey}
          onChange={handleAccordionChange(methodKey)}
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': {
              boxShadow: theme.shadows[2]
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: alpha(config.color, 0.05),
              borderRadius: '8px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: alpha(config.color, 0.1),
                color: config.color,
                mr: 2
              }}>
                {config.icon}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {config.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {config.description}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'right', mr: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Preview Value
                </Typography>
                <Typography variant="h6" fontWeight={700} color={config.color}>
                  {formatCurrency(calculatePreview(methodKey) || 0)}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {config.parameters.map((param) => renderParameterControl(methodKey, param))}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>Current Preview:</strong> {formatCurrency(calculatePreview(methodKey) || 0)}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                This is a simplified preview. Click "Recalculate Valuations" above to generate the full valuation with all considerations.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Summary of Changes */}
      {Object.keys(customAssumptions).length > 0 && (
        <Card sx={{ mt: 3, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Modified Assumptions Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The following assumptions have been modified from their default values:
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(customAssumptions).map(([methodKey, methodAssumptions]) =>
                Object.entries(methodAssumptions || {}).map(([paramKey, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={`${methodKey}-${paramKey}`}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {methodConfigs[methodKey as keyof typeof methodConfigs]?.title}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {paramKey}: {
                          typeof value === 'number' && paramKey.includes('rate') || paramKey.includes('depreciation') || paramKey.includes('growth')
                            ? formatPercentage(value as number)
                            : typeof value === 'number' && (paramKey.includes('revenue') || paramKey.includes('noi') || paramKey.includes('cost') || paramKey.includes('price'))
                            ? formatCurrency(value as number)
                            : String(value)
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                ))
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ValuationAssumptionsPanel;